import type { GatewayBrowserClient } from "../gateway.ts";
import type { UserRole } from "../storage.ts";

export type TabVisibilityMap = Record<string, Record<string, boolean>>;

export type TabVisibilityState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  tabVisibilityLoading: boolean;
  tabVisibilityMap: TabVisibilityMap;
  tabVisibilityDirty: Set<string>;
  tabVisibilitySaving: boolean;
  tabVisibilityError: string | null;
};

export const EDITABLE_ROLES: UserRole[] = ["admin", "project_admin", "user"];

export function dirtyKey(role: string, tabKey: string): string {
  return `${role}:${tabKey}`;
}

export async function loadTabVisibility(state: TabVisibilityState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.tabVisibilityLoading) {
    return;
  }
  state.tabVisibilityLoading = true;
  state.tabVisibilityError = null;
  try {
    const res = await state.client.request<{ visibility: TabVisibilityMap }>(
      "igloobot.settings.tabVisibility.get",
      {},
    );
    state.tabVisibilityMap = res.visibility ?? {};
    state.tabVisibilityDirty = new Set();
  } catch (err) {
    state.tabVisibilityError = String(err);
  } finally {
    state.tabVisibilityLoading = false;
  }
}

export async function saveTabVisibility(state: TabVisibilityState) {
  if (!state.client || !state.connected || state.tabVisibilitySaving) {
    return;
  }
  if (state.tabVisibilityDirty.size === 0) {
    return;
  }
  state.tabVisibilitySaving = true;
  state.tabVisibilityError = null;
  try {
    const entries = Array.from(state.tabVisibilityDirty);
    for (const key of entries) {
      const [role, tabKey] = key.split(":");
      const visible = state.tabVisibilityMap[role]?.[tabKey] ?? true;
      await state.client.request("igloobot.settings.tabVisibility.set", {
        role,
        tabKey,
        visible,
      });
    }
    state.tabVisibilityDirty = new Set();
  } catch (err) {
    state.tabVisibilityError = String(err);
  } finally {
    state.tabVisibilitySaving = false;
  }
}

export function toggleTabVisible(
  state: TabVisibilityState,
  role: string,
  tabKey: string,
  visible: boolean,
) {
  if (!state.tabVisibilityMap[role]) {
    state.tabVisibilityMap[role] = {};
  }
  state.tabVisibilityMap[role][tabKey] = visible;
  state.tabVisibilityDirty.add(dirtyKey(role, tabKey));
}
